// Jotai atoms for polling manager status.
//
// Each poller (created via `createPollingManager` in `polling.ts`)
// reports its status into `atomPollingStatus(name)`. React components
// can show a "syncing..." indicator; future dev tools can render a
// table of all active pollers.
//
// ADR: docs/adr/0024-polling-framework.md
// Related: ADR 0022 (Jotai), ADR 0021 (temperature), ADR 0023 (tab lock)

import { atom } from 'jotai'
import { jotaiStore } from '../jotaiStore'
import type { PollingStatus } from '../polling'

/** Map atom: name → status. Writes go through `_setPollingStatus`. */
const atomAllPollingStatuses = atom<Record<string, PollingStatus>>({})
atomAllPollingStatuses.debugLabel = 'atomAllPollingStatuses'

/** Memoised per-name accessor. Returns the same atom instance for the
 * same name so React equality checks bail out cleanly. */
const perNameCache = new Map<string, ReturnType<typeof makeAtom>>()

function makeAtom(name: string) {
  const a = atom((get) => {
    const all = get(atomAllPollingStatuses)
    return all[name] ?? null
  })
  a.debugLabel = `atomPollingStatus(${name})`
  return a
}

/** Get the atom reporting the status of a named poller. Returns null
 * when the poller hasn't been constructed yet. */
export function atomPollingStatus(name: string) {
  let a = perNameCache.get(name)
  if (!a) {
    a = makeAtom(name)
    perNameCache.set(name, a)
  }
  return a
}

/** Read-only view of every poller's current status. Useful for a dev
 * tools panel or admin console. */
export const atomAllPollers = atom((get) => get(atomAllPollingStatuses))
atomAllPollers.debugLabel = 'atomAllPollers'

/**
 * Internal: called by the polling framework to publish updates.
 * Named with leading underscore because call sites outside of
 * `polling.ts` have no business writing to this.
 */
export function _setPollingStatus(name: string, status: PollingStatus): void {
  const prev = jotaiStore.get(atomAllPollingStatuses)
  jotaiStore.set(atomAllPollingStatuses, { ...prev, [name]: status })
}
